import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { GlobalSearchService } from './globalsearchservice.service';
import { OmsService } from './oms.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

export interface Terminal {
  terminal_id: number | string | null;
  terminal_name: string;
  terminal_type?: string;
  terminal_model?: 'imove5000' | 'desk5000' | string;
  terminal_serial?: string;
  connection_type?: 'usb' | 'lan' | 'bluetooth' | 'wifi' | string;
  machine_name?: string;
  machine_user?: string;
  is_active?: boolean;
  is_available?: boolean;
  last_heartbeat?: string;
  battery_level?: number;
  signal_strength?: number;
  firmware_version?: string;
  needs_registration?: boolean;
  current_transaction_id?: number;
  connection_config?: string | any;
  location_id?: string;
}

export interface TerminalHealth {
  online: boolean;
  battery_level?: number;
  signal_strength?: number;
  firmware_version?: string;
  last_heartbeat?: string;
}

export interface TerminalTransactionRequest {
  terminal_id: number | string;
  terminal_serial?: string;
  terminal_name?: string;
  amount: number;
  transaction_type: 'sale' | 'refund' | 'void' | 'pre_auth' | 'completion' | 'settlement' | 'balance' | 'card_verify' | 'account_verify' | 'sale_cashback' | 'moto_sale' | 'moto_refund' | 'force_post' | 'force_moto' | 'force_post_moto';
  order_data?: any;
  invoice?: string;
  clerk_id?: string;
  customer_ref?: string;
  auth_code?: string;
  reference?: string;
  pan_last4?: string;
  tender_type?: 'credit' | 'debit' | 'gift' | 'cash' | 'all';
  cashback_amount?: number;
  tip_amount?: number;
  original_amount?: number;
  moto_type?: string;
  cvd?: string;
}

export interface TerminalStatus {
  transaction_id: string;
  status: 'pending' | 'processing' | 'waiting_for_card' | 'approved' | 'partial_approved' | 'declined' | 'cancelled' | 'error';
  message: string;
  terminal_id?: number | string;
  auth_code?: string;
  card_last_four?: string;
  card_type?: string;
  card_type_code?: string;
  entry_method?: 'chip' | 'swipe' | 'contactless' | 'manual' | 'chip_fallback_swipe';
  entry_mode_code?: string;
  timestamp: string;
  amount?: number;
  tip_amount?: number;
  cashback_amount?: number;
  surcharge_amount?: number;
  tax_amount?: number;
  total_amount?: number;
  invoice?: string;
  reference?: string;
  transaction_sequence?: string;
  terminal_id_response?: string;
  merchant_id?: string;
  response_code?: string;
  response_text?: string;
  receipt_data?: ReceiptData;
  emv_aid?: string;
  emv_app_label?: string;
  demo_mode?: boolean;
  batch_number?: string;
  batch_balanced?: boolean;
  batch_totals?: BatchTotals;
  amount_due?: number;
  card_balance?: number;
  client_sent_at?: number;
  node_received_at?: number;
  print_connect_received_at?: number;
  print_connect_responded_at?: number;
  node_broadcast_at?: number;
  ecr_print_success?: boolean;
  ecr_print_error?: string;
}

export interface ReceiptData {
  transactionType?: string;
  date?: string;
  time?: string;
  cardType?: string;
  accountNumber?: string;
  amount?: number;
  tip?: number;
  total?: number;
  authCode?: string;
  reference?: string;
  invoice?: string;
  terminalId?: string;
  merchantId?: string;
  entryMethod?: string;
  headers?: string[];
  footers?: string[];
  endorsements?: string[];
  demoMode?: boolean;
}

export interface BatchTotals {
  terminal?: BatchTotalRecord;
  host?: BatchTotalRecord;
  gift?: BatchTotalRecord;
}

export interface BatchTotalRecord {
  amount?: number;
  count?: number;
  saleAmount?: number;
  saleCount?: number;
  refundAmount?: number;
  refundCount?: number;
  voidAmount?: number;
  voidCount?: number;
  tipAmount?: number;
}

@Injectable({
	providedIn: 'root'
})
export class TerminalService {
	private statusSubject = new Subject<TerminalStatus>();
	public status$ = this.statusSubject.asObservable();
	private healthCheckInterval: any;
	private config: any;
	private user: any = [];

	constructor(
    private http: HttpClient,
    private globalSearchService: GlobalSearchService,
    private omsService: OmsService,
    private _configSvc: RuntimeConfigLoaderService
	) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
		});
		this.globalSearchService.user.subscribe((result) => {
			this.user = result;
		});
		this.globalSearchService.socket.subscribe((sock: any) => {
			if (sock) {
				this.setupSocketListeners();
			}
		});
		if (this.omsService.socket) {
			this.setupSocketListeners();
		}
	}

	discoverTerminals(locationId?: string, forceRefresh = false): Observable<{terminals: Terminal[], discovered_count: number, registered_count: number}> {
		const endpoint = this.setEndPoint('payments/terminal/discover');
		return this.http.post<{terminals: Terminal[], discovered_count: number, registered_count: number}>(
			endpoint,
			{ location_id: locationId, force_refresh: forceRefresh }
		);
	}

	registerTerminal(terminalData: Partial<Terminal>): Observable<{success: boolean, terminal_id: number}> {
		const endpoint = this.setEndPoint('payments/terminal/register');
		return this.http.post<{success: boolean, terminal_id: number}>(endpoint, terminalData);
	}

	getTerminalHealth(terminalId: number): Observable<TerminalHealth> {
		const endpoint = this.setEndPoint('payments/terminal/health');
		return this.http.post<TerminalHealth>(endpoint, { terminal_id: terminalId });
	}

	private emitSocketRequest<T>(eventName: string, payload: any): Observable<T> {
		return new Observable((observer) => {
			this.omsService.waitForSocketConnection().then(() => {
				const socket = this.omsService.socket;
				if (!socket || !socket.connected) {
					observer.error({ error: { message: 'Socket not connected.' } });
					return;
				}
				socket.emit(eventName, payload, (response: any) => {
					if (response?.success) {
						observer.next(response);
						observer.complete();
					} else {
						observer.error({ error: { message: response?.error || 'Request failed' } });
					}
				});
			});
		});
	}

	batchSubtotal(terminal: Terminal): Observable<any> {
		return this.runBatchAction('batch_subtotal', terminal);
	}

	batchClose(terminal: Terminal): Observable<any> {
		return this.runBatchAction('batch_close', terminal);
	}

	private runBatchAction(action: 'batch_subtotal' | 'batch_close', terminal: Terminal): Observable<any> {
		const payload = {
			terminal_id: terminal.terminal_id,
			terminal_serial: terminal.terminal_serial,
			terminal_name: terminal.terminal_name,
			amount: 0,
			transaction_type: action,
			transaction_id: 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11)
		};
		return this.emitSocketRequest('terminal_transaction_request', payload);
	}

	getPendingSyncStatus(terminal: Terminal): Observable<any> {
		const payload = {
			terminal_id: terminal?.terminal_id,
			terminal_serial: terminal?.terminal_serial,
			terminal_name: terminal?.terminal_name
		};
		return this.emitSocketRequest('terminal_queue_status_request', payload);
	}

	retryPendingSync(terminal: Terminal): Observable<any> {
		const payload = {
			terminal_id: terminal?.terminal_id,
			terminal_serial: terminal?.terminal_serial,
			terminal_name: terminal?.terminal_name
		};
		return this.emitSocketRequest('terminal_queue_retry', payload);
	}

	initiateTransaction(request: TerminalTransactionRequest): Observable<any> {
		const transaction_id = 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
		const client_sent_at = Date.now();
		const userVal = this.globalSearchService.user.getValue();
		const user = userVal?.user || userVal;
		const userid = user?.userid || null;
		const options: any = {
			transaction_type: request.transaction_type || 'sale',
			order_data: request.order_data,
			currency: 'USD',
			payment_type: request.tender_type ? request.tender_type : 'credit'
		};
		if (request.invoice != null) options.invoice = request.invoice;
		if (request.clerk_id != null) options.clerk_id = request.clerk_id;
		if (request.auth_code != null) options.auth_code = request.auth_code;
		if (request.reference != null) options.reference = request.reference;
		if (request.pan_last4 != null) options.pan_last4 = request.pan_last4;
		if (request.cvd != null) options.cvd = request.cvd;
		if (request.cashback_amount != null) options.cashback_amount = request.cashback_amount;
		if (request.tip_amount != null) options.tip_amount = request.tip_amount;
		if (request.original_amount != null) options.original_amount = request.original_amount;

		const payload: any = {
			terminal_id: request.terminal_id,
			terminal_serial: request.terminal_serial,
			terminal_name: request.terminal_name,
			amount: request.amount,
			options,
			order_data: request.order_data,
			transaction_type: request.transaction_type || 'sale',
			transaction_id,
			userid,
			client_sent_at
		};
		if (request.invoice != null) payload.invoice = request.invoice;
		if (request.clerk_id != null) payload.clerk_id = request.clerk_id;
		if (request.customer_ref != null) payload.customer_ref = request.customer_ref;
		if (request.auth_code != null) payload.auth_code = request.auth_code;
		if (request.reference != null) payload.reference = request.reference;
		if (request.pan_last4 != null) payload.pan_last4 = request.pan_last4;
		if (request.cvd != null) payload.cvd = request.cvd;
		if (request.tender_type != null) payload.tender_type = request.tender_type;
		if (request.cashback_amount != null) payload.cashback_amount = request.cashback_amount;
		if (request.original_amount != null) payload.original_amount = request.original_amount;
		if (request.moto_type != null) payload.moto_type = request.moto_type;
		return new Observable((observer) => {
			this.omsService.waitForSocketConnection().then(() => {
				const socket = this.omsService.socket;
				if (!socket || !socket.connected) {
					observer.error({ error: { message: 'Socket not connected. Cannot initiate terminal transaction.' } });
					return;
				}
				observer.next({ transaction_id });
				socket.emit('terminal_transaction_request', payload, (response: any) => {
					if (response && response.success && response.result) {
						const result = response.result;
						const statusPayload: TerminalStatus = {
							transaction_id,
							status: (result.status as TerminalStatus['status']) || 'pending',
							message: result.message || result.response_text || 'Transaction processed',
							timestamp: new Date().toISOString(),
							auth_code: result.auth_code,
							card_last_four: result.card_last_four,
							card_type: result.card_type,
							response_code: result.response_code,
							response_text: result.response_text,
							amount: result.amount || result.total_amount,
							tip_amount: result.tip_amount,
							total_amount: result.total_amount ?? result.amount,
							receipt_data: result.receipt_data,
							amount_due: result.amount_due,
							invoice: result.invoice,
							reference: result.reference,
							transaction_sequence: result.transaction_sequence,
							entry_method: result.entry_method,
							entry_mode_code: result.entry_mode_code,
							terminal_id_response: result.terminal_id,
							merchant_id: result.merchant_id,
							cashback_amount: result.cashback_amount,
							surcharge_amount: result.surcharge_amount,
							tax_amount: result.tax_amount,
							emv_aid: result.emv_aid,
							emv_app_label: result.emv_app_label,
							demo_mode: result.demo_mode,
							batch_number: result.batch_number,
							batch_balanced: result.batch_balanced,
							batch_totals: result.batch_totals,
							card_balance: result.card_balance,
							client_sent_at: result.client_sent_at,
							node_received_at: result.node_received_at,
							print_connect_received_at: result.print_connect_received_at,
							print_connect_responded_at: result.print_connect_responded_at,
							node_broadcast_at: result.node_broadcast_at,
							ecr_print_success: result.ecr_print_success !== false,
							ecr_print_error: result.ecr_print_error || undefined
						};
						if (result.ecr_print_success === false && result.ecr_print_error) {
							console.warn('[ECR_PRINT]', { transaction_id: statusPayload.transaction_id, terminal_id: statusPayload.terminal_id, ecr_print_error: result.ecr_print_error });
						}
						this.statusSubject.next(statusPayload);
						observer.complete();
					} else {
						const errMsg = (response && response.error) || 'Terminal transaction failed';
						observer.error({ error: { message: errMsg } });
					}
				});
			}).catch((error) => {
				const message = error?.message || 'Socket not connected. Cannot initiate terminal transaction.';
				observer.error({ error: { message } });
			});
		});
	}

	getTransactionStatus(transactionId: string): Observable<TerminalStatus> {
		const endpoint = this.setEndPoint('payments/terminal/status');
		return this.http.post<TerminalStatus>(endpoint, { transaction_id: transactionId });
	}

	voidTransaction(terminalTransactionId: string): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/void');
		return this.http.post(endpoint, { terminal_transaction_id: terminalTransactionId });
	}

	voidTransactionByDbId(transactionId: number): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/void');
		return this.http.post(endpoint, { transaction_id: transactionId });
	}

	processRefund(request: { original_transaction_id: string; amount: number; terminal_id: number }): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/refund');
		return this.http.post(endpoint, request);
	}

	deleteTerminal(terminalId: number): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/delete');
		return this.http.post(endpoint, { terminal_id: terminalId });
	}

	settleBatch(terminalId: number, autoSettle = false): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/initiate');
		return this.http.post(endpoint, {
			terminal_id: terminalId,
			transaction_type: autoSettle ? 'auto_settlement' : 'settlement',
			amount: 0
		});
	}

	getBalance(terminalId: number, tenderType: 'credit' | 'debit' | 'gift' = 'credit'): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/initiate');
		return this.http.post(endpoint, {
			terminal_id: terminalId,
			transaction_type: 'balance',
			amount: 0,
			tender_type: tenderType
		});
	}

	preAuthorize(request: { terminal_id: number; amount: number; invoice?: string }): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/initiate');
		return this.http.post(endpoint, {
			...request,
			transaction_type: 'pre_auth'
		});
	}

	completePreAuth(request: { terminal_id: number; auth_code?: string; reference?: string; invoice?: string; amount?: number }): Observable<any> {
		const endpoint = this.setEndPoint('payments/terminal/initiate');
		return this.http.post(endpoint, {
			...request,
			transaction_type: 'completion'
		});
	}

	startHealthMonitoring(terminalIds: number[], intervalMs = 30000) {
		if (this.healthCheckInterval) {
			clearInterval(this.healthCheckInterval);
		}

		this.healthCheckInterval = setInterval(() => {
			terminalIds.forEach(id => {
				this.getTerminalHealth(id).subscribe(
					(health) => {
						if (this.omsService.socket && this.omsService.socket.connected) {
							this.omsService.socket.emit('terminal_health_update', {
								terminal_id: id,
								health: health
							});
						}
					}
				);
			});
		}, intervalMs);
	}

	stopHealthMonitoring() {
		if (this.healthCheckInterval) {
			clearInterval(this.healthCheckInterval);
			this.healthCheckInterval = null;
		}
	}

	private setupSocketListeners() {
		const socket = this.omsService.socket;
		if (!socket) return;
		socket.off('payment_status_update');
		socket.on('payment_status_update', (data: TerminalStatus) => {
			this.statusSubject.next(data);
		});
	}

	subscribeToStatusUpdates(callback: (status: TerminalStatus) => void): void {
		this.status$.subscribe(callback);
	}

	private setEndPoint(method: any) {
		return this.getApi() + method + this.getApiKey();
	}

	private getApi() {
		return this.config?.apiServer?.crossPoint || this.config?.apiServer?.endpoint || '';
	}

	private getApiKey() {
		if (this.config?.apiCredentials?.clientId && this.user?.user?.userid) {
			return '&key=' + this.config.apiCredentials.clientId + '&userid=' + this.user.user.userid + this.getPackage();
		}
		return '';
	}

	private getPackage() {
		const p = this.globalSearchService.getSessionItem('package');
		if (p) {
			return '&package=' + p;
		} else {
			return '';
		}
	}
}
